import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

import StatusPillButtons from "components/Buttons/StatusPillButtons";

import CommonTooltip from "components/Tooltip/index";

import { renderValue } from "utils/renderCompanyCell";
import React from "react";
import Icon from "components/Icon/Icon";
import { PencilSimpleLineIcon } from "@phosphor-icons/react";

const columns = () => {
  const columnHelper = createColumnHelper();
  return [
    columnHelper.accessor("id", {
      header: "ID",
      size: 64,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: true,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),

    columnHelper.accessor("user_id", {
      header: "User ID",
      size: 120,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: true,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),

    columnHelper.accessor("status", {
      header: "Approved Status",
      size: 160,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            <StatusPillButtons status={value} dot={true} />
          </div>
        );
      }
    }),

    columnHelper.accessor("posted_by", {
      header: "Posted by",
      size: 120,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            <StatusPillButtons status={value} dot={false} />
          </div>
        );
      }
    }),

    columnHelper.accessor("is_direct_creator", {
      header: "Direct Creator",
      size: 120,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            <StatusPillButtons status={value} dot={false} />
          </div>
        );
      }
    }),

    columnHelper.accessor("is_high_value", {
      header: "High Value",
      size: 120,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            <StatusPillButtons status={value} dot={false} />
          </div>
        );
      }
    }),

    columnHelper.accessor("is_open", {
      header: "Status",
      size: 120,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            <StatusPillButtons status={value} dot={false} />
          </div>
        );
      }
    }),

    columnHelper.accessor("user_name", {
      header: "User Name",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("phone_number", {
      header: "User Phone Number",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("service_type", {
      header: "Service Type",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("brand", {
      header: "Brand",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("description", {
      header: "Description",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("platform", {
      header: "Platform",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("categories", {
      header: "Categories",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();

        const categories = Array.isArray(value)
          ? value.map((r) => r || "")
          : typeof value === "string"
            ? value.split(",").map((v) => v.trim())
            : [];

        if (categories.length === 0) return "-";
        const displayValue = categories.join(", ");

        return (
          <div className="truncate-text" title={renderValue(displayValue)}>
            {renderValue(displayValue)}
          </div>
        );
      }
    }),

    columnHelper.accessor("category_type", {
      header: "Category Type",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("gender", {
      header: "Gender",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("cities", {
      header: "Cities",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();

        const cities = Array.isArray(value)
          ? value.map((r) => r || "")
          : typeof value === "string"
            ? value.split(",").map((v) => v.trim())
            : [];

        if (cities.length === 0) return "-";

        const displayValue = cities.join(", ");

        return (
          <div className="truncate-text" title={renderValue(displayValue)}>
            {renderValue(displayValue)}
          </div>
        );
      }
    }),

    columnHelper.accessor("is_pan_india", {
      header: "Pan India",
      size: 120,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            <StatusPillButtons status={value} dot={false} />
          </div>
        );
      }
    }),

    columnHelper.accessor("creator_count_min", {
      header: "Creator Count",
      size: 160,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("creator_count_max", {
      header: "Creator Count Max",
      size: 160,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("budget", {
      header: "Budget",
      size: 160,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("budget_min", {
      header: "Budget Min",
      size: 160,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("budget_max", {
      header: "Budget Max",
      size: 160,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("languages", {
      header: "Languages",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();

        const languages = Array.isArray(value)
          ? value.map((r) => r || "")
          : typeof value === "string"
            ? value.split(",").map((v) => v.trim())
            : [];

        if (languages.length === 0) return "-";
        const displayValue = languages.join(", ");

        return (
          <div className="truncate-text" title={renderValue(displayValue)}>
            {renderValue(displayValue)}
          </div>
        );
      }
    }),

    columnHelper.accessor("any_language", {
      header: "Any language",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            <StatusPillButtons status={value} dot={false} />
          </div>
        );
      }
    }),

    columnHelper.accessor("content_deliverables", {
      header: "Content Deliverables",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("approved_at", {
      header: "Approved at",
      size: 240,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("expires_on", {
      header: "Expires on",
      size: 240,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("user_company", {
      header: "User Company",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("user_designation", {
      header: "User Designation",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("target_audience", {
      header: "Target Audience",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("user_dp", {
      header: "User Dp",
      size: 176,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="table-link truncate-text">
            <a href={value} target="_blank" rel="noopener noreferrer">
              {value}
            </a>
          </div>
        );
      }
    }),

    columnHelper.accessor("user_linkedin_url", {
      header: "Link",
      size: 176,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="table-link truncate-text">
            <a href={value} target="_blank" rel="noopener noreferrer">
              {value}
            </a>
          </div>
        );
      }
    }),

    columnHelper.accessor("collaboration_type", {
      header: "Collaboration Type",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("closed_by", {
      header: "Closed by",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("closed_via", {
      header: "Closed via",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("retool_comments", {
      header: "Comments",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("deal_closed_at", {
      header: "Deal Closed at",
      size: 176,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.accessor("close_deal_reason", {
      header: "Deal Closed via",
      size: 176,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        return (
          <div className="truncate-text" title={renderValue(info.getValue())}>
            {renderValue(info.getValue())}
          </div>
        );
      }
    }),

    columnHelper.display({
      id: "actions",
      header: "Actions",
      size: 80,
      cellClassName: "actions",
      enableSorting: false,
      cell: (info) => {
        const navigate = useNavigate();
        const campaignId = info.row.original.id;

        return (
          <div className="hover-btn-wrapper">
            <CommonTooltip theme="dark" tooltipContent="Edit Details">
              <div
                className="hover-actions"
                onClick={() =>
                  navigate(
                    `/marketplace/edit-campaign/${campaignId}${window.location.search}`,
                    {
                      state: {
                        campaign: info.row.original
                      }
                    }
                  )
                }
              >
                <Icon
                  component={PencilSimpleLineIcon}
                  format="outline"
                  weight="regular"
                />
              </div>
            </CommonTooltip>
          </div>
        );
      }
    })
  ];
};

export default columns;
